import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { Draggable, Droppable } from '@hello-pangea/dnd';

import { v4 as uuid } from 'uuid';

import { Control, UseFormRegister } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import type { TabItem } from './types';
import LayoutItems from './LayoutItems';
import {
  FormItem, InquiryForm, LayoutItem,
} from '../../../models/Inquiry/Inquiry';
import config from '../../../config';
import { encrypt } from '../../../utils/encryption';

export const webDbTabs: TabItem[] = [
  { name: '一覧レイアウト', value: 'list', tabval: 1 },
  { name: '詳細レイアウト', value: 'detail', tabval: 2 },
];

interface WebDbTabContentsProps {
  items: FormItem[];
  currentTab: number;
  formFields: FormItem[];
  formValues: InquiryForm;
  listFields: LayoutItem[][];
  detailFields: LayoutItem[][];
  control: Control<InquiryForm, any>;
  register: UseFormRegister<InquiryForm>;
}

// CUSTOM COMPONENTS ==========================================================================================================================================
const ListWithDroppable = ({
  items,
  header,
  droppable,
  previewUrl,
  formValues,
  listFields,
  detailFields,
}: {
  header: string;
  items: FormItem[];
  droppable: JSX.Element;
  previewUrl: string;
  formValues: InquiryForm;
  listFields?: LayoutItem[][];
  detailFields?: LayoutItem[][];
}) => {
  const navigateToPreview = () => {
    const data = {
      ...formValues,
      list_layout: listFields || [],
      details_layout: detailFields || [],
    };

    if (data) window.sessionStorage.setItem(config.preview.storageKey, encrypt(JSON.stringify(data)));
    window.open(previewUrl, '_blank');
  };

  return (
    <div className="row" style={{ minHeight: 300 }}>
      <div className="col-8 d-flex flex-column" style={{ alignSelf: 'stretch' }}>
        <div className="d-flex justify-content-between align-items-center py-2">
          <div className="fw-bold mb-1">{header}</div>

          <button className="btn btn-primary text-sm px-3" type="button" onClick={navigateToPreview}>
            <span className="mx-2">レイアウトプレビュー表示</span>
            <FontAwesomeIcon icon={faUpRightFromSquare} />
          </button>
        </div>

        <div className="flex-fill">
          {droppable}
        </div>

        <div style={{
          fontSize: '80%',
          color: '#6c757d',
          marginTop: '0.25rem',
        }}
        >
          ※空白を入れることで1行表示になります。
        </div>
      </div>

      <div className="col-4" style={{ height: '100%' }}>
        <LayoutItems
          data={items}
          isLoading={false}
          droppableId="webdb-list-layout-items"
        />
      </div>
    </div>
  );
};

const SplitDroppable = ({
  type,
  fields,
}: {
  type: 'list' | 'detail'
  fields: LayoutItem[][];
  control: Control<InquiryForm, any>
  register: UseFormRegister<InquiryForm>
  disabled?: boolean;
}) => (
  <div
    className="row m-0 gap-0 h-100"
    style={{
      padding: '0.5rem',
      border: '1px dashed #ccc',
      minHeight: '92px',
    }}
  >
    {Array.from({ length: 2 }).map((_, index) => (
      <div className="col-6 p-0" key={uuid()} style={{ alignSelf: 'stretch' }}>
        <Droppable droppableId={`webdb-list-viewitems-${type}-${index}`}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              className={`webdb-list-viewitems webdb-layout-drop flex-fill ${snapshot.isDraggingOver ? 'bg-light' : ''}`}
              style={{ border: 'none', minHeight: '100%' }}
            >
              <div className="list-group justify-content-center">
                {fields[index]?.map((item, itemIndex) => (
                  <Draggable
                    key={item.uuid}
                    index={itemIndex}
                    draggableId={String(item.uuid)}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className="panel-item"
                      >
                        <span className="flex-fill mb-2">
                          <span className="me-2">
                            <FontAwesomeIcon icon={faBars} />
                          </span>

                          {item.label}
                        </span>
                      </div>
                    )}
                  </Draggable>
                ))}
              </div>

              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    ))}
  </div>
);

// MAIN COMPONENT =============================================================================================================================================
const WebDbTabContents = ({
  items,
  register,
  control,
  listFields,
  formFields,
  currentTab,
  formValues,
  detailFields,
}: WebDbTabContentsProps) => {
  const [listUsableItems, setListUsableItems] = useState<FormItem[]>([]);
  const [detailUsableItems, setDetailUsableItems] = useState<FormItem[]>([]);

  const filterList = useCallback((filterArray: LayoutItem[][]) => {
    const usables: FormItem[] = [];

    // Check which item is used in formFields
    const itemsUsedInForm = items?.map((item) => {
      const isItemUsedInFormFields = formFields.some((field) => field.label === item.label);
      if (!isItemUsedInFormFields) return { ...item, hide: false };

      return item;
    });

    // Filter items based on formFields
    formFields.forEach((field) => {
      const item = itemsUsedInForm?.find((i) => i.label === field.label);
      // Verify it item if being used in listFields or detailFields
      const isBeingUsed = filterArray.flat().some((i) => i.uuid === field.uuid);

      if (
        item
        && !item.hide
        && !isBeingUsed
      ) {
        usables.push({
          ...field,
          hide: false,
        });
      }
    });

    return usables;
  }, [items, formFields]);

  useEffect(() => {
    setListUsableItems(filterList(listFields));
    setDetailUsableItems(filterList(detailFields));
  }, [items, formFields, filterList, listFields, detailFields]);

  if (currentTab === 1) {
    return (
      <div className={`tab-pane ${currentTab === 1 ? 'active' : ''}`} role="tabpanel">
        <ListWithDroppable
          items={listUsableItems}
          header="＜一覧ページ 表示レイアウト＞"
          // Preview
          formValues={formValues}
          listFields={listFields}
          detailFields={detailFields}
          previewUrl="/inquiry-management/preview/layout/list"
          // Droppable
          droppable={(
            <SplitDroppable
              type="list"
              fields={listFields}
              control={control}
              register={register}
            />
          )}
        />
      </div>
    );
  }

  if (currentTab === 2) {
    return (
      <div className={`tab-pane ${currentTab === 2 ? 'active' : ''}`} role="tabpanel">
        <ListWithDroppable
          items={detailUsableItems}
          header="＜詳細ページ 表示レイアウト＞"
          // Preview
          formValues={formValues}
          listFields={listFields}
          detailFields={detailFields}
          previewUrl="/inquiry-management/preview/layout/detail"
          // Droppable
          droppable={(
            <SplitDroppable
              type="detail"
              fields={detailFields}
              control={control}
              register={register}
            />
          )}
        />
      </div>
    );
  }

  return null;
};

export default WebDbTabContents;
